import { type Combatant, type Move } from '@/game/battle';

export type HeroTemplateId = 'lucy' | 'java-man' | 'mungo' | 'cro-magnon' | 'otzi';

export interface HeroVisual {
  tintR: number;
  tintG: number;
  tintB: number;
  scale: number;
}

export interface HeroTemplate {
  id: HeroTemplateId;
  name: string;
  era: string;
  seed: number;
  baseHp: number;
  attack: number;
  defense: number;
  /** Canonical moveset (2-4). The first move is the hero's "signature". */
  moves: ReadonlyArray<Move>;
  contributions: ReadonlyArray<string>;
  blurb: string;
  visual: HeroVisual;
}

export const STONE_AGE_HEROES: ReadonlyArray<HeroTemplate> = [
  {
    id: 'lucy',
    name: 'Lucy',
    era: 'Australopithecus afarensis · ~3.2 Mya',
    seed: 11,
    baseHp: 24,
    attack: 7,
    defense: 3,
    moves: [
      { id: 'forage', name: 'Forage', basePower: 6, accuracy: 1.0 },
      { id: 'quick-stride', name: 'Quick Stride', basePower: 8, accuracy: 0.9 },
    ],
    contributions: ['bipedalism', 'foraging'],
    blurb: 'Among the earliest to walk upright across the open savanna.',
    visual: { tintR: 0.62, tintG: 0.52, tintB: 0.42, scale: 1.05 },
  },
  {
    id: 'java-man',
    name: 'Java Man',
    era: 'Homo erectus · ~1 Mya',
    seed: 22,
    baseHp: 30,
    attack: 8,
    defense: 2,
    moves: [
      { id: 'spark', name: 'Spark', basePower: 8, accuracy: 0.95 },
      { id: 'hand-axe', name: 'Hand Axe', basePower: 11, accuracy: 0.8 },
    ],
    contributions: ['fire', 'hand-axe', 'hunting'],
    blurb: 'Walked from Africa to Java. Mastered fire and the Acheulean axe.',
    visual: { tintR: 0.86, tintG: 0.76, tintB: 0.62, scale: 1.3 },
  },
  {
    id: 'mungo',
    name: 'Mungo',
    era: 'Lake Mungo, Homo sapiens · ~42 kya',
    seed: 44,
    baseHp: 26,
    attack: 8,
    defense: 3,
    moves: [
      { id: 'red-ochre', name: 'Red Ochre', basePower: 7, accuracy: 0.95 },
      { id: 'ancestor-song', name: 'Ancestor Song', basePower: 5, accuracy: 1.0 },
    ],
    contributions: ['burial-rites', 'ochre-pigment', 'symbolic-thought'],
    blurb: 'Earliest ceremonial burial; the first sparks of symbolic mind.',
    visual: { tintR: 0.55, tintG: 0.42, tintB: 0.32, scale: 1.25 },
  },
  {
    id: 'cro-magnon',
    name: 'Cro-Magnon',
    era: 'Aurignacian Europe · ~30 kya',
    seed: 55,
    baseHp: 28,
    attack: 9,
    defense: 2,
    moves: [
      { id: 'cave-mark', name: 'Cave Mark', basePower: 8, accuracy: 0.95 },
      { id: 'atlatl', name: 'Throwing Stick', basePower: 10, accuracy: 0.85 },
    ],
    contributions: ['cave-art', 'sewn-clothing', 'atlatl'],
    blurb: 'Painters of Chauvet; tailored hides, spear-thrown across the steppe.',
    visual: { tintR: 0.95, tintG: 0.8, tintB: 0.62, scale: 1.35 },
  },
  {
    id: 'otzi',
    name: 'Ötzi',
    era: 'Late Neolithic · ~5300 ya',
    seed: 33,
    baseHp: 22,
    attack: 10,
    defense: 2,
    moves: [
      { id: 'flint-strike', name: 'Flint Strike', basePower: 10, accuracy: 0.85 },
      { id: 'bow-shot', name: 'Bow Shot', basePower: 7, accuracy: 1.0 },
    ],
    contributions: ['stone-tools', 'metal-use', 'ranged-weapons'],
    blurb: 'A Copper-Age traveler caught between epochs, carrying the first metals.',
    visual: { tintR: 1.0, tintG: 0.82, tintB: 0.6, scale: 1.4 },
  },
];

export const heroById = (id: HeroTemplateId): HeroTemplate => {
  const h = STONE_AGE_HEROES.find((x) => x.id === id);
  if (h === undefined) throw new Error(`unknown hero template: ${id}`);
  return h;
};

export const combatantFromHero = (template: HeroTemplate): Combatant => ({
  name: template.name,
  maxHp: template.baseHp,
  hp: template.baseHp,
  attack: template.attack,
  defense: template.defense,
  moves: template.moves,
});
